import { useQuery } from '@apollo/client';
import { Plus, User, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomerFormDialog } from '@/modules/customers-pets/CustomerFormDialog';
import { CUSTOMERS_QUERY, CUSTOMER_QUERY } from '@/modules/customers-pets/customers.gql';
import { PetFormDialog } from '@/modules/customers-pets/PetFormDialog';
import { PETS_QUERY, SEARCH_PETS_QUERY } from '@/modules/customers-pets/pets.gql';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';

interface CustomerLite {
  id: string;
  first_name: string;
  last_name: string | null;
  phone: string;
}

interface PetLite {
  id: string;
  customer_id: string;
  name: string;
  species: string | null;
  is_deceased: number;
  status: 'active' | 'inactive';
}

interface PetSearchHit extends PetLite {
  record: { code: string } | null;
  customer: CustomerLite;
}

interface CustomersPage {
  customers: { items: CustomerLite[] };
}
interface PetsPage {
  pets: { items: PetLite[] };
}
interface PetSearchPage {
  searchPets: PetSearchHit[];
}

interface CustomerPetPickerProps {
  customer_id: string | null;
  pet_id: string | null;
  onChange: (next: { customer_id: string | null; pet_id: string | null }) => void;
  disabled?: boolean;
}

export function CustomerPetPicker({
  customer_id,
  pet_id,
  onChange,
  disabled,
}: CustomerPetPickerProps): JSX.Element {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [show_create_customer, setShowCreateCustomer] = useState(false);
  const [show_create_pet, setShowCreatePet] = useState(false);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(search.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [search]);

  const { data: customers_data, loading: customers_loading } = useQuery<CustomersPage>(
    CUSTOMERS_QUERY,
    {
      variables: { offset: 0, limit: 8, search: debounced || null, status_filter: 'ACTIVE' },
      skip: !!customer_id || debounced.length < 2,
    },
  );

  const { data: customer_record } = useQuery<{ customer: CustomerLite }>(CUSTOMER_QUERY, {
    variables: { id: customer_id ?? '' },
    skip: !customer_id,
  });

  const { data: pet_search_data, loading: pet_search_loading } = useQuery<PetSearchPage>(
    SEARCH_PETS_QUERY,
    {
      variables: { search: debounced, limit: 6 },
      skip: !!customer_id || debounced.length < 2,
    },
  );

  const { data: pets_data, refetch: refetchPets } = useQuery<PetsPage>(PETS_QUERY, {
    variables: {
      offset: 0,
      limit: 30,
      customer_id: customer_id ?? null,
      include_deceased: false,
      status_filter: 'ACTIVE',
    },
    skip: !customer_id,
  });

  const selected_customer = customers_data?.customers.items.find((c) => c.id === customer_id)
    ?? customer_record?.customer
    ?? null;

  const onClickClearCustomer = (): void => {
    onChange({ customer_id: null, pet_id: null });
    setSearch('');
  };

  const onClickPickCustomer = (c: CustomerLite): void => {
    onChange({ customer_id: c.id, pet_id: null });
  };

  const onClickPickPet = (p: PetLite): void => {
    if (!customer_id) return;
    onChange({ customer_id, pet_id: p.id });
  };

  const onClickPickPetSearchHit = (hit: PetSearchHit): void => {
    onChange({ customer_id: hit.customer_id, pet_id: hit.id });
    setSearch('');
  };

  const selected_pet = pets_data?.pets.items.find((p) => p.id === pet_id) ?? null;

  return (
    <div className="space-y-3">
      {customer_id && selected_customer ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-card/40 p-3">
          <User className="h-4 w-4 text-muted-foreground" aria-hidden />
          <div className="flex-1">
            <p className="font-medium">
              {selected_customer.first_name}
              {selected_customer.last_name ? ` ${selected_customer.last_name}` : ''}
            </p>
            <p className="font-mono text-xs text-muted-foreground">{selected_customer.phone}</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClickClearCustomer}
            disabled={disabled}
          >
            <X className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <Input
            type="search"
            placeholder={t('doctor_pos.customer_search_placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={disabled}
          />
          {debounced.length >= 2 ? (
            <div className="space-y-1">
              {customers_loading ? (
                <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
              ) : null}
              {customers_data?.customers.items.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onClickPickCustomer(c)}
                  disabled={disabled}
                  className="flex w-full items-center justify-between rounded-md border border-transparent px-3 py-2 text-left text-sm hover:border-border hover:bg-secondary"
                >
                  <span>
                    {c.first_name}
                    {c.last_name ? ` ${c.last_name}` : ''}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">{c.phone}</span>
                </button>
              ))}
              {pet_search_data?.searchPets.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onClickPickPetSearchHit(p)}
                  disabled={disabled}
                  className="flex w-full items-center justify-between rounded-md border border-transparent px-3 py-2 text-left text-sm hover:border-border hover:bg-secondary"
                >
                  <span className="flex items-center gap-2">
                    <span>{p.name}</span>
                    {p.species ? <Badge variant="muted">{p.species}</Badge> : null}
                    {p.record?.code ? (
                      <span className="font-mono text-xs text-muted-foreground">
                        {p.record.code}
                      </span>
                    ) : null}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {p.customer.first_name}
                    {p.customer.last_name ? ` ${p.customer.last_name}` : ''}
                  </span>
                </button>
              ))}
              {(customers_data?.customers.items.length ?? 0) === 0 &&
              (pet_search_data?.searchPets.length ?? 0) === 0 &&
              !customers_loading &&
              !pet_search_loading ? (
                <p className="text-xs text-muted-foreground">{t('doctor_pos.no_customer_match')}</p>
              ) : null}
            </div>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowCreateCustomer(true)}
            disabled={disabled}
          >
            <Plus className="h-4 w-4" aria-hidden />
            <span>{t('customers.create')}</span>
          </Button>
        </div>
      )}

      {customer_id ? (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('doctor_pos.pet_label')}
          </p>
          <div className="flex flex-wrap gap-2">
            {pets_data?.pets.items.map((p) => (
              <Button
                key={p.id}
                type="button"
                size="sm"
                variant={p.id === pet_id ? 'default' : 'outline'}
                onClick={() => onClickPickPet(p)}
                disabled={disabled}
              >
                {p.name}
                {p.species ? <Badge variant="muted" className="ml-2">{p.species}</Badge> : null}
              </Button>
            ))}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setShowCreatePet(true)}
              disabled={disabled}
            >
              <Plus className="h-4 w-4" aria-hidden />
              <span>{t('pets.create')}</span>
            </Button>
          </div>
          {selected_pet?.is_deceased === 1 ? (
            <Badge variant="muted">{t('pets.deceased')}</Badge>
          ) : null}
        </div>
      ) : null}

      {show_create_customer ? (
        <CustomerFormDialog
          mode="create"
          onClose={() => setShowCreateCustomer(false)}
          onSaved={(id) => {
            setShowCreateCustomer(false);
            onChange({ customer_id: id, pet_id: null });
          }}
        />
      ) : null}
      {show_create_pet && customer_id ? (
        <PetFormDialog
          mode="create"
          customer_id={customer_id}
          onClose={() => setShowCreatePet(false)}
          onSaved={async (new_pet_id) => {
            setShowCreatePet(false);
            await refetchPets();
            if (new_pet_id) {
              onChange({ customer_id, pet_id: new_pet_id });
            }
          }}
        />
      ) : null}
    </div>
  );
}
